import {useTranslation} from 'react-i18next';
import {
  CommandeProblem,
  LigneProblem,
  Panier,
  RapportMatch,
} from 'api-types/ffconnect';
import {UseHalStateHook, useHalState} from '@synako/haligator';
import {PanierValiderOptionsDesc} from 'api-types/ffconnect-hal';

import {InfoIcon} from '@chakra-ui/icons';
import {HStack, Text, VStack} from '@chakra-ui/layout';
import {Divider, Input, Textarea, Tooltip} from '@chakra-ui/react';

import {toDecimalFr, useReferenceCommande} from 'ff-common';
import {PanierCommands} from 'ff-common/src/hooks/panier/use-panier-commands';
import {Navigable} from '@synako/halitrek';
import {PaiementOptionDesc} from 'api-types/ffconnect-hal';

import {PanierRecapDate} from './PanierRecapDate';
import {PanierRecapMarketplaceDate} from './PanierRecapMarketplaceDate';
import {PanierRecapAdresse} from './PanierRecapAdresse';
import {PanierRecapValider} from './PanierRecapValider';
import {PanierRecapStatutTolede} from './PanierRecapStatutTolede';
import {MargesCommande} from '../MargesCommande';
import {useState} from 'react';
import {useSocieteCommerciale} from '@/haligator-factory';

export const PanierRecapitulatif: React.FC<{
  panier: Panier;
  panierWithCommands: PanierCommands;
  validerOptions: UseHalStateHook<PanierValiderOptionsDesc>;
  setCommandeProblems: (problems: CommandeProblem[]) => void;
  setLigneProblems: (problems: LigneProblem[]) => void;
  paiementOptionNav: Navigable<PaiementOptionDesc>;
  rapportMatch?: RapportMatch; // Panier PDF : rapport de matching
  isPanierPdf: boolean; // Panier PDF : la validation renvoie vers la liste des imports du BO
}> = ({
  panier,
  panierWithCommands,
  validerOptions,
  setCommandeProblems,
  setLigneProblems,
  paiementOptionNav,
  rapportMatch,
  isPanierPdf,
}) => {
  const isMarketplace =
    panier.type_panier !== undefined && panier.type_panier !== 'FF';
  // const panierData = panierWithCommands.panier.data;
  const {t} = useTranslation();
  const [commentaireClient, setCommentaireClient] = useState<string>('');
  const soc = useHalState(useSocieteCommerciale());

  // Gestion de la référence de commande
  const {isReferenceError, ifReferenceRequired, handleReferenceChange} =
    useReferenceCommande({
      validerOptions: validerOptions,
      changerReferenceCommand: panierWithCommands.changerReference,
    });

  // Panier PDF : la référence a été extraite du bon de commande — encadré
  // d'alerte pour que l'opérateur la vérifie avant validation.
  const referenceDepuisPdf =
    rapportMatch?.numero_bon_commande_client !== undefined;

  // Panier PDF : écart entre le total du bon de commande et le total calculé
  // au matching. Les valeurs viennent du rapport, jamais recalculées, l'écart peut donc être figé par rapport au panier courant.
  // Un bon de commande sans total (le PDF n'en porte pas, ou il est à zéro)
  // donne un rapport à zéro en 'alerte' : il n'y a alors aucun écart à annoncer.
  const coherencePrix = rapportMatch?.coherence_prix_total;
  const ecartPrixPdf =
    coherencePrix &&
    coherencePrix.etat !== 'ok' &&
    coherencePrix.prix_total_pdf !== 0
      ? coherencePrix
      : undefined;
  // Les anciens rapports n'ont pas type_total : HT par défaut
  const typeTotalPdf = ecartPrixPdf?.type_total ?? 'ht';
  const ecartPrixRow = ecartPrixPdf && (
    <HStack
      w='100%'
      justify='start'
      textColor='yellow.warning_text'
    >
      <Text fontSize='sm'>
        {t('back_office.pdf_order_import.match.prix_total_incoherent', {
          ecart: ecartPrixPdf.ecart_pourcentage.toFixed(1),
        })}
      </Text>
      <Text
        ml='auto'
        fontSize='sm'
      >
        {typeTotalPdf === 'ttc'
          ? t('back_office.pdf_order_import.match.prix_total_pdf_ttc')
          : t('back_office.pdf_order_import.match.prix_total_pdf')}{' '}
        {toDecimalFr(ecartPrixPdf.prix_total_pdf)}&nbsp;€
      </Text>
    </HStack>
  );

  return (
    <VStack
      bg='white'
      p='12px'
      pt={{base: '10', lg: '10'}}
      gap='3'
      border='1px solid'
      borderColor='gray.light'
      borderRadius='12px'
      boxShadow='md'
      alignItems='stretch'
      position='sticky'
      top='0'
      textColor='gray.dark'
      fontSize='md'
      flexDirection={{base: 'column-reverse', lg: 'column'}}
      maxW={{base: '100%', lg: '380px'}}
      ml={{base: 0, lg: 'auto'}}
      // mb='10'
    >
      <Text
        display={{base: 'none', lg: 'block'}}
        fontWeight='bold'
        textColor='black'
        fontSize='lg'
        alignSelf='center'
        borderRadius='8px'
        mb='5'
      >
        {t('panier.recap.recapitulatif')}
      </Text>
      <VStack
        gap='3'
        alignItems='stretch'
      >
        <HStack
          w='100%'
          justify='start'
        >
          <Text fontSize='sm'>{t('panier.recap.total_ht')}</Text>
          <Tooltip label={t('panier.recap.tooltip_total_ht')}>
            <InfoIcon
              width='16px'
              height='16px'
            />
          </Tooltip>
          <Text
            ml='auto'
            fontSize='sm'
          >
            {toDecimalFr(panier.total_estime_ht)}&nbsp;€
          </Text>
        </HStack>
        {typeTotalPdf === 'ht' && ecartPrixRow}
        {panier.surcout_ht && (
          <HStack
            w='100%'
            justify='start'
          >
            <Text fontSize='sm'>{t('panier.recap.surcout_ht')}</Text>
            <Text
              ml='auto'
              fontSize='sm'
            >
              {toDecimalFr(panier.surcout_ht)}&nbsp;€
            </Text>
          </HStack>
        )}
        <HStack
          w='100%'
          justify='start'
        >
          <Text fontSize='sm'>{t('panier.recap.total_tva')}</Text>
          <Text
            ml='auto'
            fontSize='sm'
          >
            {toDecimalFr(panier.total_estime_tva)}&nbsp;€
          </Text>
        </HStack>
        <HStack
          w='100%'
          justify='start'
          textColor='black'
        >
          <Text
            fontWeight='600'
            fontSize='md'
          >
            {t('panier.recap.total_ttc')}
          </Text>
          <Text
            ml='auto'
            fontWeight='700'
            fontSize='lg'
          >
            {toDecimalFr(panier.total_estime_ttc)}&nbsp;€
          </Text>
        </HStack>
        {typeTotalPdf === 'ttc' && ecartPrixRow}
        {!isMarketplace && (
          <MargesCommande
            panierNav={panierWithCommands.panier}
            fontSize='xs'
            textAlign='right'
          />
        )}
      </VStack>
      <PanierRecapStatutTolede />
      <PanierRecapValider
        validerCommand={panierWithCommands.valider}
        verifierQuantites={panierWithCommands.verifierQuantites}
        validerOptions={validerOptions}
        setCommandeProblems={setCommandeProblems}
        setLigneProblems={setLigneProblems}
        commentaireClient={commentaireClient}
        validationAdresseLivraison={panier.validation_adresse_livraison}
        changerModeDeRenduOptions={
          panierWithCommands.changerModeDeRendu.options
        }
        changerModeDeRenduSubmit={panierWithCommands.changerModeDeRendu.submit}
        codeAdresse={panier.livraison_tolede.code_adresse_de_livraison!}
        modeDeRendu={panier.livraison_tolede.mode_de_rendu}
        paiementOptionNav={paiementOptionNav}
        isPanierPdf={isPanierPdf}
      />
      <Divider m='3' />
      <VStack
        gap='3'
        alignItems='stretch'
        bg={referenceDepuisPdf ? 'yellow.warning' : undefined}
        p={referenceDepuisPdf ? '3' : undefined}
        borderRadius={referenceDepuisPdf ? '8px' : undefined}
      >
        <Text fontSize='sm'>
          {ifReferenceRequired
            ? t('panier.recap.reference_commande_needed')
            : t('panier.recap.reference_commande')}
        </Text>
        <Input
          isInvalid={isReferenceError}
          isRequired={isReferenceError}
          bg={referenceDepuisPdf ? 'white' : undefined}
          borderColor={referenceDepuisPdf ? 'yellow.warning_text' : undefined}
          defaultValue={panier.reference}
          onChange={(event) => {
            handleReferenceChange(event.target.value);
          }}
        />
      </VStack>
      {soc.data?.permettre_commentaire_commande && (
        <VStack
          gap='3'
          alignItems='stretch'
        >
          <Text>{t('panier.recap.commentaire_commande')}</Text>
          <Textarea
            value={commentaireClient}
            onChange={(event) => {
              setCommentaireClient(event.target.value);
            }}
            placeholder={t('panier.recap.commentaire_commande_placeholder')}
          />
        </VStack>
      )}
      <Divider
        m='3'
        display={{base: 'block', lg: 'none'}}
      />
      <VStack
        gap='3'
        alignItems='stretch'
      >
        {isMarketplace ? (
          <PanierRecapMarketplaceDate />
        ) : (
          <PanierRecapDate
            date_de_livraison_ou_d_enlevement={
              panier.livraison_tolede.date_de_livraison
            }
            changerDateCommand={panierWithCommands.changerDateDeLivraison}
            matchingDate={rapportMatch?.date_de_livraison}
          />
        )}
        {panier.livraison_tolede.code_adresse_de_livraison && (
          <PanierRecapAdresse
            panier={panier}
            panierWithCommands={panierWithCommands}
            matchingAdresse={rapportMatch?.adresse_de_livraison}
          />
        )}
      </VStack>
    </VStack>
  );
};
